/**
 * TanStack Query hooks for the in-app book (ebook-plan § 3.13). The state decides whether any
 * book UI shows (`visibility`), so a flag flip on the server needs no rebuild. Chapters are kept
 * in memory for 10 minutes and never persisted; the next chapter is fetched ahead only on request.
 */
import { useCallback } from 'react';
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { bookApi } from '@/services/api/book';
import type { BookState, ProgressIn } from '@/services/api/bookContract';
import { queryKeys, retryUnlessClientError } from '@/config/queryClient';
import { useIsAuthenticated, useUser } from '@/store';

const CHAPTER_CACHE_MS = 10 * 60 * 1000;
const STATE_STALE_MS = 60 * 1000;

/** A payload that fails the contract will fail again: no retry. Otherwise the app's rule. */
export function retryBookQuery(failureCount: number, error: unknown): boolean {
  if (error instanceof z.ZodError) return false;
  return retryUnlessClientError(failureCount, error);
}

function useBookUserId(): string {
  return useUser()?.id ?? '';
}

/** `GET /api/book` for the signed-in account; disabled when signed out. */
export function useBookState() {
  const isAuthenticated = useIsAuthenticated();
  const userId = useBookUserId();
  return useQuery({
    queryKey: queryKeys.book.state(userId),
    queryFn: bookApi.getState,
    enabled: isAuthenticated,
    staleTime: STATE_STALE_MS,
    retry: retryBookQuery,
  });
}

/** True only when the server says the book is visible for this account (D14). */
export function useIsBookVisible(): boolean {
  const { data } = useBookState();
  return data?.visibility === 'visible';
}

function chapterQuery(userId: string, chapterId: string) {
  return {
    queryKey: queryKeys.book.chapter(userId, chapterId),
    queryFn: () => bookApi.getChapter(chapterId),
    staleTime: CHAPTER_CACHE_MS,
    gcTime: CHAPTER_CACHE_MS,
    refetchOnWindowFocus: false,
    retry: retryBookQuery,
  } as const;
}

export function useBookChapter(chapterId: string | undefined) {
  const isAuthenticated = useIsAuthenticated();
  const userId = useBookUserId();
  return useQuery({ ...chapterQuery(userId, chapterId ?? ''), enabled: isAuthenticated && !!chapterId });
}

/** Fetch a chapter ahead (the next one, at 60 % of the current one; § 3.8). */
export function usePrefetchBookChapter(): (chapterId: string) => void {
  const queryClient = useQueryClient();
  const userId = useBookUserId();
  return useCallback(
    (chapterId: string) => {
      void queryClient.prefetchQuery(chapterQuery(userId, chapterId));
    },
    [queryClient, userId],
  );
}

function rememberProgress(queryClient: QueryClient, userId: string, progress: ProgressIn): void {
  queryClient.setQueryData<BookState>(queryKeys.book.state(userId), (state) =>
    state && state.visibility === 'visible'
      ? {
          ...state,
          progress: {
            chapterId: progress.chapterId,
            blockId: progress.blockId,
            progression: progress.progression,
            totalProgression: progress.totalProgression,
            updatedAt: progress.clientUpdatedAt,
          },
        }
      : state,
  );
}

/**
 * Saves the reading position (last write wins on the server). `save` is the normal write;
 * `saveOnHide` is the keepalive write for a page being hidden. Both also update the cached
 * state, so the overview's "Nastavi čitanje" points at the new place. A failed write is
 * dropped: the next one carries a newer position anyway.
 */
export function useSaveBookProgress() {
  const queryClient = useQueryClient();
  const userId = useBookUserId();
  const save = useCallback(
    (progress: ProgressIn) => {
      rememberProgress(queryClient, userId, progress);
      bookApi.saveProgress(progress).catch(() => undefined);
    },
    [queryClient, userId],
  );
  const saveOnHide = useCallback(
    (progress: ProgressIn) => {
      rememberProgress(queryClient, userId, progress);
      bookApi.saveProgressOnHide(progress);
    },
    [queryClient, userId],
  );
  return { save, saveOnHide };
}
